// 사업장 대표 업종(ksic_category) 단일 소스.
// 백엔드 ksic_category 는 자유 문자열로 저장되며, 미지 값은 '기타'처럼 graceful 처리됨.
import type { KsicCategory } from '../types/sites'

export const KSIC_OPTIONS: { value: KsicCategory; label: string; example: string }[] = [
  { value: 'manufacturing', label: '제조업', example: '전자·기계·식품·금속·섬유 등' },
  { value: 'construction', label: '건설업', example: '건물·토목·전기·설비공사' },
  { value: 'chemical', label: '화학·정유', example: '화학물질·정유·도금·고무·플라스틱' },
  { value: 'mining', label: '광업', example: '채굴·채석·골재' },
  { value: 'electricity_gas', label: '전기·가스·증기', example: '발전·가스·증기·수도' },
  { value: 'environment', label: '환경·폐기물', example: '폐기물 처리·재활용·환경복원' },
  { value: 'agriculture', label: '농업·임업·어업', example: '경작·축산·임업·수산' },
  { value: 'transport', label: '운수·물류·창고', example: '운송·물류·창고·하역' },
  { value: 'wholesale_retail', label: '도소매·유통', example: '도매·소매·유통·판매' },
  { value: 'food_lodging', label: '숙박·음식점', example: '호텔·식당·급식·카페' },
  { value: 'healthcare', label: '보건·의료·복지', example: '병원·요양·사회복지' },
  { value: 'it', label: '정보통신·IT', example: '소프트웨어·통신·데이터센터' },
  { value: 'education', label: '교육서비스', example: '학교·학원·교육기관' },
  { value: 'finance', label: '금융·보험', example: '은행·보험·증권' },
  { value: 'realestate', label: '부동산·임대', example: '부동산·임대·관리' },
  { value: 'professional', label: '전문·과학·기술', example: '엔지니어링·연구·설계' },
  { value: 'facility_support', label: '시설관리·사업지원', example: '청소·경비·조경·사업지원' },
  { value: 'arts_sports', label: '예술·스포츠·여가', example: '공연·체육·오락시설' },
  { value: 'service', label: '기타 서비스업', example: '수리·개인서비스 등' },
  { value: 'other', label: '기타 (직접 입력)', example: '목록에 없는 업종 직접 입력' },
]

// ksic_category 코드 → 한글 라벨 (표시용 fallback: ksic_name 이 비었을 때)
export const KSIC_CATEGORY_LABELS: Record<string, string> =
  Object.fromEntries(KSIC_OPTIONS.map((o) => [o.value, o.label]))

// 대표 업종(ksic_category)별 '업종 상세' 추천 목록 — 골라 입력하거나 직접 입력(기타) 항상 가능.
export const KSIC_SUB_OPTIONS: Record<string, string[]> = {
  manufacturing: [
    '자동차 부품 제조업', '금속가공제품 제조업', '전자부품·반도체 제조업', '전기장비 제조업',
    '일반 기계·장비 제조업', '식료품 제조업', '음료 제조업', '섬유·의복 제조업', '고무·플라스틱 제조업',
    '1차 금속(제철·제강·주조) 제조업', '비금속 광물제품(시멘트·유리) 제조업', '가구 제조업',
    '의료기기 제조업', '인쇄·기록매체 제조업', '목재·종이 제조업', '조선·기타 운송장비 제조업',
  ],
  construction: [
    '건물 건설업(아파트·상가)', '토목 건설업(도로·교량·터널)', '전기공사업', '정보통신공사업',
    '기계설비공사업', '실내건축·인테리어 공사업', '철근·콘크리트 공사업', '비계·구조물 해체공사업',
    '도장·방수 공사업', '조경 건설업', '상하수도·플랜트 공사업', '철강구조물 공사업',
  ],
  chemical: [
    '기초화학물질 제조업', '석유정제품 제조업', '합성수지·플라스틱물질 제조업', '도료·페인트 제조업',
    '의약품 제조업', '화장품 제조업', '비료·농약 제조업', '도금·표면처리업', '고무제품 제조업',
    '세제·계면활성제 제조업', '산업용 가스 제조업',
  ],
  mining: ['석탄·금속광물 채굴업', '비금속광물 채석업', '골재·모래 채취업', '광물 선별·가공업'],
  electricity_gas: ['발전업(화력·수력·신재생)', '송배전업', '도시가스업', '증기·냉온수 공급업', '수도사업'],
  environment: ['생활·사업장 폐기물 처리업', '폐기물 재활용업', '하·폐수 처리업', '토양·환경 복원업', '재활용 원료 가공업'],
  agriculture: ['작물 재배업', '축산업', '작물·축산 복합농업', '임업·벌목업', '어로·양식 어업', '농업 지원 서비스업'],
  transport: ['도로화물 운송업', '택배·배송업', '창고·보관업', '하역·화물취급업', '여객 운송업', '항공·항만 지원 서비스업', '냉장·냉동 창고업'],
  wholesale_retail: ['종합 도매업', '산업용 기계·자재 도매업', '대형마트·슈퍼마켓', '전자상거래 소매업', '건축자재 유통업', '자동차·부품 판매업'],
  food_lodging: ['일반음식점업', '구내식당·단체급식업', '호텔·숙박업', '카페·제과점', '주점업', '출장 음식 서비스업'],
  healthcare: ['종합병원·병원', '의원·치과·한의원', '요양병원·요양시설', '사회복지시설', '장례·보건 관련 서비스업'],
  it: ['소프트웨어 개발·공급업', '정보통신·통신업', '데이터센터·호스팅업', '게임 소프트웨어업', '시스템 통합(SI)업'],
  education: ['초·중·고등 교육기관', '대학·고등교육기관', '학원·교습소', '직업훈련기관', '평생교육·기타 교육서비스업'],
  finance: ['은행·신용기관', '보험업', '증권·자산운용업', '여신·금융지원 서비스업'],
  realestate: ['부동산 임대·공급업', '부동산 중개·관리업', '시설 임대업'],
  professional: ['건축·엔지니어링 서비스업', '연구개발업', '기술 시험·분석업', '법무·회계 서비스업', '디자인·설계 서비스업'],
  facility_support: ['건물 청소·방역업', '경비·보안 서비스업', '조경 관리업', '시설 유지관리업', '사업시설 종합관리업', '콜센터·사업지원 서비스업'],
  arts_sports: ['공연·예술 시설 운영업', '스포츠시설 운영업', '오락·여가 시설 운영업', '유원지·테마파크업'],
  service: ['자동차 정비·수리업', '기계·전자제품 수리업', '세탁업', '이·미용업', '개인 서비스업'],
  other: [],
}
